import React, { useState, useEffect } from 'react';
import { 
  Card,
  CardContent,
  Chip, 
  Typography, 
  Box,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import TenantShareEdit from './TenantShareEdit';
import { useUser } from '../hooks/useUser';
import { useApiClient } from '../hooks/useApiClient';

interface TenantShare {
  email: string;
  permissionProfile: 'reader' | 'poweruser' | 'admin' | 'owner';
  createdAt: string;
  updatedAt: string;
}

interface TenantSharesListProps {
  tenantId: string;
  tenantName?: string;
}

const TenantSharesList: React.FC<TenantSharesListProps> = ({ tenantId, tenantName }) => {
  const [shares, setShares] = useState<TenantShare[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingEmail, setEditingEmail] = useState<string | null>(null);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState<string | null>(null);
  const { data: currentUser } = useUser();
  const apiClient = useApiClient();

  console.log('TenantSharesList - Props received:', { tenantId, tenantName });
  console.log('TenantSharesList - tenantId type:', typeof tenantId);
  console.log('TenantSharesList - tenantId value:', tenantId);

  useEffect(() => {
    const fetchShares = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('TenantSharesList - Fetching shares for tenant:', tenantId);
        
        const response = await apiClient.get(`/tenants/${tenantId}/shares`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch shares: ${response.statusText}`);
        }

        const data = await response.json();
        
        setShares(data.shares || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load shares');
      } finally {
        setLoading(false);
      }
    };

    if (tenantId) {
      fetchShares();
    } else {
      setLoading(false);
    }
  }, [tenantId, apiClient]);



  const getPermissionColor = (permission: string) => {
    switch (permission) {
      case 'owner':
        return 'secondary';
      case 'admin':
        return 'error';
      case 'poweruser':
        return 'warning';
      case 'reader':
        return 'info';
      default:
        return 'default';
    }
  };

  const handleShareSave = (savedShare: TenantShare) => {
    if (editingEmail) {
      setShares(prev => prev.map(s => s.email === editingEmail ? savedShare : s));
      setEditingEmail(null);
    } else {
      setShares(prev => [...prev, savedShare]);
      setShowCreateForm(false);
    }
  };

  const handleEditShare = (email: string) => {
    setEditingEmail(email);
    setShowCreateForm(false);
  };

  const handleCancelEdit = () => {
    setEditingEmail(null);
    setShowCreateForm(false);
  };

  const handleDeleteShare = async (email: string) => {
    try {
      const response = await apiClient.delete(`/tenants/${tenantId}/shares/${encodeURIComponent(email)}`);

      if (response.ok) {
        setShares(prev => prev.filter(s => s.email !== email));
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete share');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete share');
    } finally {
      setDeleteConfirmEmail(null);
    }
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmEmail) {
      handleDeleteShare(deleteConfirmEmail);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Tenant Shares
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setShowCreateForm(true)}
          size="small"
        >
          Add Share
        </Button>
      </Box>
      
      {showCreateForm && (
        <Box sx={{ mb: 2 }}>
          <TenantShareEdit
            tenantId={tenantId}
            onSave={handleShareSave}
            onCancel={handleCancelEdit}
          />
        </Box>
      )}
      
      {shares.length === 0 && !showCreateForm ? (
        <Typography variant="body2" color="text.secondary">
          No shares found for this tenant.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {shares.map((share) => (
            <Box key={share.email}>
              {editingEmail === share.email ? (
                <TenantShareEdit
                  tenantId={tenantId}
                  share={share}
                  onSave={handleShareSave}
                  onCancel={handleCancelEdit}
                />
              ) : (
                <Card variant="outlined">
                  <CardContent sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                          {share.email}
                        </Typography>
                        {currentUser?.email === share.email && (
                          <Chip 
                            label="You"
                            color="primary"
                            size="small"
                            variant="filled"
                          />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label={share.permissionProfile}
                          color={getPermissionColor(share.permissionProfile) as any}
                          size="small"
                          variant="outlined"
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleEditShare(share.email)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => setDeleteConfirmEmail(share.email)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Typography variant="body2" color="text.secondary">
                        Created: {new Date(share.createdAt).toLocaleDateString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Updated: {new Date(share.updatedAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>
          ))}
        </Stack>
      )}
      
      <Dialog
        open={!!deleteConfirmEmail}
        onClose={() => setDeleteConfirmEmail(null)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the share for {deleteConfirmEmail}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmEmail(null)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantSharesList;